import Swal from 'sweetalert2'
import axios from 'axios'
import {
  AssinaturaType,
  EnvioType,
  ModalSize,
  PrescricaoSituation,
  showAlert,
  showAlertLoading,
  showConfirmAlert,
  showError,
  TipoReceitaSncr,
} from '../../../../../../_metronic/helpers'
import { useModal } from '../../../../../components'
import { useT } from '../../../../../../_metronic/i18n/Metronici18n'
import { NecessidadesSncr, useSncr } from '../../../sncr/hooks/useSncr'
import { SNCR_CODIGO, idEmpresaSncrDe } from '../../../sncr/core/_models'
import { codigoErroSncr } from '../../../sncr/core/erros'
import { PrescricaoDtoDadosPaciente, PrescricaoView } from '../core/_model'
import {
  completePrescricao,
  deletePrescricao,
  getPrescricaoById,
  sendPrescricao,
} from '../core/_requests'
import { PdfModal } from '../components/modal/PdfModal'
import { EnviarPrescricaoModal } from '../components/modal/EnviarPrescricaoModal'
import { AtendimentoView } from '../../../atendimento/core/_models'
import { useCertificado } from '../../../../../helpers/hooks/useCertificado'

/** Tentativas de finalizar a mesma prescrição depois de retirar bloco SNCR (evita laço com backend inconsistente). */
const MAX_TENTATIVAS_SNCR = 3

export const usePrescricaoUtils = (atendimento?: AtendimentoView) => {
  const { showModal } = useModal()
  const { ensurePassword } = useCertificado()
  const { ensureSncr } = useSncr()
  const { t } = useT()

  const idEmpresaSncr = idEmpresaSncrDe(atendimento)

  /**
   * Quantos números SNCR cada tipo vai consumir ao finalizar estes rascunhos. Guarda em
   * `tiposExigidos` o tipo de cada rascunho para o retry de saldo não buscá-lo de novo. Se a
   * consulta falhar, fecha o loading antes de propagar, para o erro do chamador não ficar atrás dele.
   */
  const necessidadesSncr = async (
    ids: number[],
    tiposExigidos: Map<number, TipoReceitaSncr | undefined>
  ): Promise<NecessidadesSncr> => {
    const necessidades: NecessidadesSncr = {}
    if (!idEmpresaSncr || ids.length === 0) return necessidades
    try {
      const views = await Promise.all(ids.map((id) => getPrescricaoById(id)))
      for (const view of views) {
        const tipo = view.tipoReceitaSncrExigido ?? undefined
        tiposExigidos.set(view.id, tipo)
        if (tipo && view.situacao === PrescricaoSituation.Rascunho) {
          necessidades[tipo] = (necessidades[tipo] ?? 0) + 1
        }
      }
    } catch (error) {
      Swal.close()
      throw error
    }
    return necessidades
  }

  /**
   * Finaliza uma prescrição. Sem saldo SNCR (422 `SALDO_INSUFICIENTE`) oferece a retirada e repete o
   * mesmo id; `null` quando o médico desiste (o rascunho fica). Uma prescrição que já saiu de
   * rascunho (reenvio depois de uma interrupção) é devolvida como está em vez de falhar.
   */
  const completarPrescricao = async (
    id: number,
    tipo: AssinaturaType,
    tiposExigidos: Map<number, TipoReceitaSncr | undefined>
  ): Promise<PrescricaoView | null> => {
    for (let tentativa = 0; tentativa < MAX_TENTATIVAS_SNCR; tentativa++) {
      try {
        return await completePrescricao({ prescricao: id, dadosPaciente: null, tipo })
      } catch (error) {
        const codigo = codigoErroSncr(error)
        if (codigo !== SNCR_CODIGO.SALDO_INSUFICIENTE) {
          if (axios.isAxiosError(error) && error.response?.status === 422) {
            const view = await getPrescricaoById(id)
            if (view.situacao !== PrescricaoSituation.Rascunho) return view
          }
          throw error
        }
        Swal.close()
        // Sem empresa o ensureSncr liberaria em silêncio e o laço só falharia depois de 3 voltas.
        if (!idEmpresaSncr) throw new Error(t('sncr.erro.empresa_indefinida'))
        const tipoSncr = tiposExigidos.has(id)
          ? tiposExigidos.get(id)
          : (await getPrescricaoById(id)).tipoReceitaSncrExigido
        const liberado = tipoSncr ? await ensureSncr(idEmpresaSncr, { [tipoSncr]: 1 }) : false
        if (!liberado) return null
        showAlertLoading()
      }
    }
    // `null` é só desistência explícita; esgotar as tentativas é falha e precisa aparecer na tela.
    throw new Error(t('sncr.erro.saldo_persistente'))
  }

  const showPrescricoes = (prescricoes: PrescricaoView[]) => {
    Swal.close()
    showModal({
      title: prescricoes.length > 1 ? 'Documentos gerados' : 'Documento gerado',
      content: <PdfModal />,
      size: ModalSize.Large,
      fetchers: {
        prescricoes: async () => prescricoes,
      },
    })
  }

  const fetchAllGeradas = async (geradas: number[]) => {
    const resultados = []
    try {
      const responses = await Promise.all(
        geradas.map(async (id) => {
          const prescricao = await getPrescricaoById(id)
          return prescricao
        })
      )

      resultados.push(...responses)
    } catch (error) {
      showError(error)
    }

    return resultados
  }

  const handleSignPrescriptions = async (prescricoes: number[]) => {
    // Receita controlada precisa de número SNCR: garante o saldo antes de pedir a senha. Desistir
    // aqui preserva os rascunhos (diferente do cancelamento da senha, logo abaixo).
    const tiposExigidos = new Map<number, TipoReceitaSncr | undefined>()
    const necessidades = await necessidadesSncr(prescricoes, tiposExigidos)
    if (Object.keys(necessidades).length > 0) {
      Swal.close()
      const liberado = await ensureSncr(idEmpresaSncr, necessidades)
      if (!liberado) return
      showAlertLoading()
    }

    const { ok, tipo } = await ensurePassword()
    if (!ok || !tipo) {
      for (const id of prescricoes) {
        await deletePrescricao(id)
      }
      Swal.close()
      return
    }
    const geradas: PrescricaoView[] = []

    for (const id of prescricoes) {
      const prescGerada = await completarPrescricao(id, tipo, tiposExigidos)
      if (!prescGerada) {
        // Desistiu no meio: mostra o que já saiu e deixa o restante como rascunho para "Assinar" de novo.
        Swal.close()
        if (geradas.length > 0) showPrescricoes(geradas)
        return
      }
      geradas.push(prescGerada)
    }

    const isConfirmed = await showConfirmAlert({
      confirmButtonText: 'Sim',
      title: 'Enviar prescrição',
      text: 'Você gostaria de enviar?',
    })
    if (isConfirmed) await enviarPrescricoes(prescricoes)

    Swal.close()
    showAlert({
      title: 'Sucesso!',
      text: 'Prescrição assinada com sucesso.',
      icon: 'success',
    })

    const ids = geradas.map((geradas) => geradas.id)
    const generated = await fetchAllGeradas(ids)
    showPrescricoes(generated)

    return generated
  }

  const showEnviarModal = (email: string | undefined, telefone: string | undefined) =>
    new Promise<{ email: string; telefone: string } | null>((resolve) => {
      showModal({
        title: 'Dados para envio',
        content: <EnviarPrescricaoModal onConfirm={resolve} />,
        size: ModalSize.Medium,
        fetchers: {
          email: async () => email,
          telefone: async () => telefone,
        },
        onClose: () => resolve(null),
      })
    })

  const enviarPrescricoes = async (ids: number[]) => {
    if (ids.length > 0) {
      Swal.close()
      const userData = await showEnviarModal(
        atendimento?.paciente?.pessoa?.usuario?.email,
        atendimento?.paciente?.pessoa?.telefone
      )
      showAlert({ title: 'Enviando prescrição', showLoading: true, icon: 'info' })
      const dadosPaciente: PrescricaoDtoDadosPaciente = {
        telefone: userData?.telefone || null,
        email: userData?.email || atendimento?.paciente?.pessoa?.usuario?.email || null,
        tipoEnvio: EnvioType.Ambos,
      }
      for (const id of ids) {
        await sendPrescricao(id, dadosPaciente)
      }
    }
  }

  return {
    handleSignPrescriptions,
    showPrescricoes,
    showEnviarModal,
    enviarPrescricoes,
    fetchAllGeradas,
  }
}
